import { useCallback, useMemo } from 'react';
import { lessonRunsApi } from '../../services/apiV2';

interface UseLessonOpsActionsParams {
  runId?: string;
}

export const useLessonOpsActions = ({ runId }: UseLessonOpsActionsParams) => {
  const runIdNumber = useMemo(() => {
    const parsed = Number(runId);
    if (!Number.isInteger(parsed) || parsed <= 0) return null;
    return parsed;
  }, [runId]);

  const requireRunId = useCallback(() => {
    if (!runIdNumber) {
      throw new Error('Missing run id');
    }
    return runIdNumber;
  }, [runIdNumber]);

  const loadSubmissions = useCallback((stepId?: number | null) => (
    lessonRunsApi.submissions(requireRunId(), stepId ?? undefined)
  ), [requireRunId]);

  const loadAdaptiveState = useCallback(() => (
    lessonRunsApi.adaptiveState(requireRunId())
  ), [requireRunId]);

  const loadAdaptiveSuggestions = useCallback((status?: string) => (
    lessonRunsApi.adaptiveSuggestions(requireRunId(), status)
  ), [requireRunId]);

  const generateAdaptiveSuggestions = useCallback(() => (
    lessonRunsApi.generateAdaptiveSuggestions(requireRunId())
  ), [requireRunId]);

  const approveAdaptiveSuggestion = useCallback((suggestionId: number, payload?: { apply?: boolean }) => (
    lessonRunsApi.approveAdaptiveSuggestion(requireRunId(), suggestionId, payload)
  ), [requireRunId]);

  const rejectAdaptiveSuggestion = useCallback((suggestionId: number, payload?: { decision_note?: string }) => (
    lessonRunsApi.rejectAdaptiveSuggestion(requireRunId(), suggestionId, payload)
  ), [requireRunId]);

  const applyAdaptiveSuggestion = useCallback((
    suggestionId: number,
    payload?: { allow_stale?: boolean; overrides?: Record<string, any> },
  ) => (
    lessonRunsApi.applyAdaptiveSuggestion(requireRunId(), suggestionId, payload)
  ), [requireRunId]);

  const clearStudentOverrides = useCallback((
    payload:
      | { return_to: 'class_step' | 'origin_step'; student_ids?: number[] }
      | { target_step_id: number; student_ids?: number[] },
  ) => (
    lessonRunsApi.clearStudentOverrides(requireRunId(), payload)
  ), [requireRunId]);

  const updateAudience = useCallback((studentIds: number[], mode: 'join' | 'leave') => (
    lessonRunsApi.audience(requireRunId(), studentIds, mode)
  ), [requireRunId]);

  const requestMicroAssist = useCallback((data: { prompt?: string; action_type?: string; context?: any }) => (
    lessonRunsApi.aiMicroAssist(requireRunId(), data)
  ), [requireRunId]);

  const requestTeacherAi = useCallback((data: {
    surface: 'class_draft' | 'class_draft_chat' | 'teacher_walkthrough' | 'teacher_chat';
    step_id?: number;
    draft_mode?: 'quick_explain' | 'step_by_step' | 'hint_only';
    walkthrough_mode?: 'teacher_deep' | 'student_friendly';
    walkthrough_adjustment?: 'standard' | 'shorter' | 'more_scaffolding';
    question?: string;
    parent_event_id?: number;
    include_lesson_context?: boolean;
  }) => (
    lessonRunsApi.teacherAiGenerate(requireRunId(), data)
  ), [requireRunId]);

  const loadTeacherAiThread = useCallback((params: {
    surface: 'class_draft_chat' | 'teacher_chat';
    step_id?: number;
  }) => (
    lessonRunsApi.teacherAiThread(requireRunId(), params)
  ), [requireRunId]);

  const submitAssistFeedback = useCallback((
    assistId: number,
    payload: { usefulness_vote?: 'up' | 'down' | null; used_in_class?: boolean | null },
  ) => (
    lessonRunsApi.aiAssistFeedback(requireRunId(), assistId, payload)
  ), [requireRunId]);

  const loadAssistRankings = useCallback(() => (
    lessonRunsApi.aiAssistFeedbackSummary(requireRunId())
  ), [requireRunId]);

  return useMemo(() => ({
    runIdNumber,
    loadSubmissions,
    loadAdaptiveState,
    loadAdaptiveSuggestions,
    generateAdaptiveSuggestions,
    approveAdaptiveSuggestion,
    rejectAdaptiveSuggestion,
    applyAdaptiveSuggestion,
    clearStudentOverrides,
    updateAudience,
    requestMicroAssist,
    requestTeacherAi,
    loadTeacherAiThread,
    submitAssistFeedback,
    loadAssistRankings,
  }), [
    runIdNumber,
    loadSubmissions,
    loadAdaptiveState,
    loadAdaptiveSuggestions,
    generateAdaptiveSuggestions,
    approveAdaptiveSuggestion,
    rejectAdaptiveSuggestion,
    applyAdaptiveSuggestion,
    clearStudentOverrides,
    updateAudience,
    requestMicroAssist,
    requestTeacherAi,
    loadTeacherAiThread,
    submitAssistFeedback,
    loadAssistRankings,
  ]);
};
